import { getter } from "@progress/kendo-data-query";
import { Button, DropDownButton, DropDownButtonItem, Toolbar } from "@progress/kendo-react-buttons";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { FieldWrapper } from "@progress/kendo-react-form";
import { getSelectedState, Grid, GridColumn as Column, GridHeaderSelectionChangeEvent, GridNoRecords, GridSelectionChangeEvent, GridCustomCellProps } from "@progress/kendo-react-grid";
import { Checkbox } from "@progress/kendo-react-inputs";
import { Label } from '@progress/kendo-react-labels';
import { Upload as KendoUpload, UploadFileInfo, UploadHandle } from "@progress/kendo-react-upload";
import DateCell from "components/cells/DateCell";
import LoadingPanel from "components/LoadingPanel";
import useConfirm from "components/useConfirm";
import dayjs from 'dayjs';
import { createRef, useCallback, useContext, useEffect, useState } from "react";
import { HubConnectionBuilder, LogLevel } from "@microsoft/signalr";
import { LoadOneReconnectPolicy } from "services/signalr/retryPolicy";
import { useParams } from "react-router";
import { fetchApi, fetchApiFormData } from "services/api";
import { openWindowPerc } from "services/openWindow";
import { OrderImage } from "TypeGen/Order/Images/order-image";
import { ViewModel } from "TypeGen/Order/Images/view-model";
import { JsonResponse } from "TypeGen/json-response";
import { OrderContext } from "../Details";
import { CheckInPaperwork } from "./CheckInPaperwork";
import { EmailPaperwork } from "./EmailPaperwork";
import { SvgIcon } from "@progress/kendo-react-common";
import { caretAltDownIcon, downloadIcon, infoCircleIcon } from "@progress/kendo-svg-icons";
import CustomDropZone from "./CustomDropZone";
import ImageLightbox from "./ImageLightbox";
import { useIsMobile } from "utils/useIsMobile";

type RouteComponentParams = {
  orderId: string;
};

const DATA_ITEM_KEY: string = "ImageID";
const SELECTED_FIELD: string = "selected";
const idGetter = getter(DATA_ITEM_KEY);

const ImageDateCell = (props: GridCustomCellProps) => {
  if (!props.field || props.rowType !== 'data') return null;

  const dataItem: OrderImage = props.dataItem;

  //don't convert to local EST, already in EST
  return (
    <td {...props.tdProps}>
      <span>{dayjs.utc(dataItem.ImageDate).format("MM/DD/YYYY")}</span>
    </td>
  );
};

const OrderImages = () => {
  const uploadRef = createRef<UploadHandle>();
  const { orderId: orderIdParam } = useParams<RouteComponentParams>();
  const { setActiveStopIds, setDeleteStopIds } = useContext(OrderContext);
  const { ConfirmationDialog, confirm } = useConfirm({});
  const [loading, setLoading] = useState(true);
  const [details, setDetails] = useState<ViewModel>();
  const [showCheckInPaperwork, setShowCheckInPaperwork] = useState(false);
  const [emailImageIds, setEmailImageIds] = useState<number[] | null>(null);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const isMobile = useIsMobile();

  // Selection
  const [selectedState, setSelectedState] = useState<{
    [id: string]: boolean | number[];
  }>({});
  const selectedIds = Object.entries(selectedState).filter(([, value]) => value === true).map(([key]) => parseInt(key));

  // Upload
  const [files, setFiles] = useState<UploadFileInfo[]>([]);
  const [paperworkType, setPaperworkType] = useState(0);
  const [printWithInvoice, setPrintWithInvoice] = useState<boolean | null>(null);
  const [uploading, setUploading] = useState(false);

  const fetchDetails = useCallback(async () => {
    setLoading(true);
    const response = await fetchApi(`/api/Order/Images/${orderIdParam}`);
    response.Images = response.Images.map((item: OrderImage) => ({ ...item, inEdit: true, Edited: false }))
    setDetails(response);
    setLoading(false);
  }, [orderIdParam]);

  useEffect(() => {
    ((document.getElementsByClassName("k-upload-button")[0] as HTMLElement).getElementsByTagName("span")[0] as HTMLElement).innerText = "Select file...";
    ((document.getElementsByClassName("k-dropzone")[0] as HTMLElement).getElementsByClassName("k-dropzone-hint")[0] as HTMLElement).innerText = "Drag and drop files, or paste from your clipboard using Ctrl + V";

    setActiveStopIds([]);
    setDeleteStopIds([]);
    fetchDetails();
  }, [fetchDetails, setActiveStopIds, setDeleteStopIds]);

  useEffect(() => {
    const connection = new HubConnectionBuilder()
      .withAutomaticReconnect(new LoadOneReconnectPolicy())
      .withUrl("/hub/order")
      .configureLogging(LogLevel.Warning)
      .build();

    connection.onreconnected(() => {
      fetchDetails();
    });

    connection.on('imageIndexed', (event: { OrderID: number }) => {
      if (event.OrderID.toString() === orderIdParam) {
        fetchDetails();
      }
    });

    connection.start().catch((error) => console.error(error));

    return () => {
      connection.stop();
    }
  }, [fetchDetails, orderIdParam]);

  const handleUpload = async () => {

    if (files.length == 0) {
      alert("Please select a file to upload.");
      return;
    }

    if (paperworkType == 0) {
      alert("Please select a paperwork type.");
      return;
    }

    setUploading(true);

    try {
      const uploadPromises = files.map(uploadFile);
      await Promise.all(uploadPromises);

      setFiles([]);
    } catch (e: any) {
      // If not problem details
      if (!e?.status) alert(e);
      return;
    } finally {
      setUploading(false);
    }
    fetchDetails();
  }

  const uploadFile = async (file: UploadFileInfo) => {
    const formData = new FormData();
    formData.append("File", file.getRawFile());
    formData.append('FileNameExtension', file.extension);
    formData.append('PrintWithInvoice', printWithInvoice ? 'true' : 'false');
    formData.append('PaperworkType', paperworkType.toString());

    return fetchApiFormData(`/api/Image/Upload/${orderIdParam}`, formData, 'POST');
  }

  const handleCheckInPaperwork = async (bolIn: boolean, podIn: boolean, date: Date) => {
    setUploading(true);

    try {
      await fetchApi(`/api/Order/CheckInPaperwork/${orderIdParam}`, { BOLIn: bolIn, PODIn: podIn, Date: date }, 'POST');
    } catch (e) {
      alert(e);
      return;
    } finally {
      setUploading(false);
    }

    setShowCheckInPaperwork(false);
    fetchDetails();
  }

  const handleUndoCheckInPaperwork = async () => {

    if (await confirm(`Are you sure you want to undo paperwork check in?`) == false) {
      return;
    }

    setUploading(true);

    try {
      await fetchApi(`/api/Order/UndoCheckInPaperwork/${orderIdParam}`);
    } catch (e) {
      alert(e);
      return;
    } finally {
      setUploading(false);
    }

    fetchDetails();
  }

  const itemChange = useCallback((dataItem: OrderImage, field: keyof OrderImage, value: unknown) => {
    setDetails(current => current ? {
      ...current,
      Images: current.Images.map((item) =>
        item.ImageID === dataItem.ImageID
          ? { ...item, Edited: true, [field]: value }
          : item
      )
    } : current);
  }, []);

  const DocumentTypeCell = useCallback((props: GridCustomCellProps) => (
    <td {...props.tdProps} className={[props.tdProps?.className, "k-edit-cell"].filter(Boolean).join(" ")}>
      <DropDownList
        data={details?.PaperworkTypes}
        value={details?.PaperworkTypes.find(x => x.ID == props.dataItem.TypeOfPW)}
        onChange={(e) => itemChange(props.dataItem, "TypeOfPW", e.target.value.ID)}
        textField="Name"
        dataItemKey="ID"
      />
    </td>
  ), [details?.PaperworkTypes, itemChange]);

  const PrintWithInvoiceCell = useCallback((props: GridCustomCellProps) => (
    <td {...props.tdProps}>
      <Checkbox
        checked={props.dataItem.PrintFlag}
        onChange={(e) => itemChange(props.dataItem, "PrintFlag", e.value)}
      />
    </td>
  ), [itemChange]);

  const updateItem = async (dataItem: OrderImage) => {
    const data = {
      TypeOfPW: dataItem.TypeOfPW,
      PrintFlag: dataItem.PrintFlag,
    };
    await fetchApi(`/api/Image/Update/${dataItem.ImageID}`, data, 'PUT');

    fetchDetails();
  }

  const updateLightboxImage = async (
    imageId: number,
    data: { TypeOfPW: number; PrintFlag: boolean }
  ) => {
    try {
      const updateResponse: JsonResponse = await fetchApi(`/api/Image/Update/${imageId}`, data, 'PUT');
      if (!updateResponse.Success) {
        alert(updateResponse.ErrorMessage);
        return false;
      }

      const refreshed = await fetchApi(`/api/Order/Image/${imageId}`);
      setDetails(current => current ? {
        ...current,
        Images: current.Images.map(item =>
          item.ImageID === imageId
            ? { ...refreshed.Image, inEdit: true, Edited: false }
            : item
        )
      } : current);
      return true;
    } catch (e: any) {
      if (!e?.status) alert(e);
      else if (e.status !== 404) alert(e.detail);
      return false;
    }
  };

  const deleteLightboxImage = async (imageId: number) => {
    if (!window.confirm("Are you sure you want to delete this image?")) {
      return false;
    }

    try {
      const deleteResponse: JsonResponse = await fetchApi(`/api/Image/Delete/${imageId}`, {}, 'DELETE');
      if (!deleteResponse.Success) {
        alert(deleteResponse.ErrorMessage);
        return false;
      }

      setDetails(current => current ? {
        ...current,
        Images: current.Images.filter(item => item.ImageID !== imageId)
      } : current);
      setSelectedState(current => {
        const next = { ...current };
        delete next[imageId];
        return next;
      });
      return true;
    } catch (e: any) {
      if (!e?.status) alert(e);
      else if (e.status !== 404) alert(e.detail);
      return false;
    }
  };

  const deleteSelected = async () => {

    if (await confirm(`Are you sure you want to delete ${selectedIds.length} ${selectedIds.length === 1 ? 'image' : 'images'}?`) == false) {
      return;
    }

    setUploading(true);

    try {
      for (let i = 0; i < selectedIds.length; i++) {
        await fetchApi(`/api/Image/Delete/${selectedIds[i]}`, {}, 'DELETE');
      }
      setSelectedState({});
    } catch (e: any) {
      // If not problem details
      if (!e?.status) alert(e);
      return;
    } finally {
      setUploading(false);
    }

    fetchDetails();
  }

  const updateSelected = async (data: { TypeOfPW?: number, PrintFlag?: boolean }) => {

    if (await confirm(`Are you sure you want to update ${selectedIds.length} ${selectedIds.length === 1 ? 'image' : 'images'}?`) == false) {
      return;
    }

    setUploading(true);

    try {
      for (let i = 0; i < selectedIds.length; i++) {
        await fetchApi(`/api/Image/Update/${selectedIds[i]}`, data, 'PUT');
      }
      setSelectedState({});
    } catch (e: any) {
      // If not problem details
      if (!e?.status) alert(e);
      return;
    } finally {
      setUploading(false);
    }

    fetchDetails();
  }

  const onSelectionChange = useCallback(
    (event: GridSelectionChangeEvent) => {
      if (event.startColIndex === 0) {
        const newSelectedState = getSelectedState({
          event,
          selectedState: selectedState,
          dataItemKey: DATA_ITEM_KEY,
        });
        setSelectedState(newSelectedState);
      }
    },
    [selectedState]
  );

  const onHeaderSelectionChange = useCallback(
    (event: GridHeaderSelectionChangeEvent) => {
      const checkboxElement: any = event.syntheticEvent.target;
      const checked = checkboxElement.checked;
      const newSelectedState = {} as any;

      event.dataItems.forEach((item) => {
        newSelectedState[idGetter(item)] = checked;
      });
      setSelectedState(newSelectedState);
    },
    []
  );

  return <>
    <CustomDropZone uploadRef={uploadRef} />
    {showCheckInPaperwork && <CheckInPaperwork
      orderId={orderIdParam}
      loading={uploading}
      save={handleCheckInPaperwork}
      onClose={() => setShowCheckInPaperwork(false)}
    />}
    {emailImageIds && <EmailPaperwork
      orderId={orderIdParam}
      selectedIds={emailImageIds}
      onClose={() => setEmailImageIds(null)}
    />}
    {loading && <LoadingPanel />}
    {lightboxIndex !== null && details && <ImageLightbox
      images={details.Images}
      paperworkTypes={details.PaperworkTypes}
      startIndex={lightboxIndex}
      onClose={() => setLightboxIndex(null)}
      onUpdate={updateLightboxImage}
      onDelete={deleteLightboxImage}
      onEmail={(imageId) => setEmailImageIds([imageId])}
    />}
    <ConfirmationDialog />
    <div className="row">
      <div className="col-lg-6 offset-lg-3">
        <KendoUpload
          ref={uploadRef}
          batch={false}
          files={files}
          autoUpload={false}
          disabled={uploading}
          restrictions={{
            allowedExtensions: ['.jpeg', '.jpg', '.png', '.gif', '.heic', '.heif', '.pdf']
          }}
          onAdd={(e) => setFiles(e.newState)}
          onRemove={(e) => setFiles(e.newState)}
          showActionButtons={false}
        />
        <p className="p-1 mb-2">File types supported: .jpeg, .jpg, .png, .gif, .heic, .heif, .pdf</p>
        <FieldWrapper className="mb-3">
          <Label>Paperwork Type</Label>
          <DropDownList
            disabled={uploading}
            data={details?.PaperworkTypes}
            value={details?.PaperworkTypes.find(x => x.ID == paperworkType)}
            onChange={(e) => {
              const newPaperworkTypeId = e.target.value.ID
              setPaperworkType(newPaperworkTypeId);
              setPrintWithInvoice(newPaperworkTypeId == 1 || newPaperworkTypeId == 2 || newPaperworkTypeId == 3 || newPaperworkTypeId == 6);
            }}
            defaultItem={{ Name: "Select a Paperwork Type" }}
            valid={paperworkType > 0}
            textField="Name"
            dataItemKey="ID"
          />
        </FieldWrapper>
        <Checkbox
          disabled={uploading}
          value={printWithInvoice}
          onChange={(e) => setPrintWithInvoice(e.value)}
          className="mr-1 float-right"
          label="Print w/ Invoice"
        />
        <Button
          themeColor="primary"
          className="mb-3 center-block"
          disabled={uploading}
          onClick={handleUpload}
        >
          Upload Image
        </Button>
        {details?.Links.find(x => x.Name === "CheckInPaperwork") && files.length == 0 && <Button
          className="mb-3 ml-2 center-block"
          disabled={uploading}
          onClick={() => setShowCheckInPaperwork(true)}
        >
          Check In Paperwork
        </Button>}
        {details?.Links.find(x => x.Name === "UndoCheckInPaperwork") && <Button
          className="mb-3 ml-2 center-block "
          themeColor={'warning'}
          disabled={uploading}
          onClick={() => handleUndoCheckInPaperwork()}
        >
          Undo Check In Paperwork
        </Button>}
      </div>
    </div>
    {selectedIds.length > 0 && <Toolbar>
      <Button onClick={() => setSelectedState({})}>Deselect</Button>
      <DropDownButton
        themeColor="primary"
        text="Print w/ Invoice"
        icon="arrow-60-down"
        svgIcon={caretAltDownIcon}
        onItemClick={(e) => updateSelected({ PrintFlag: e.itemIndex === 0 ? true : false })}
      >
        <DropDownButtonItem text="Print" />
        <DropDownButtonItem text="Do Not Print" />
      </DropDownButton>
      <DropDownButton
        themeColor="primary"
        text="Document Type"
        icon="arrow-60-down"
        svgIcon={caretAltDownIcon}
        items={details?.PaperworkTypes}
        textField="Name"
        onItemClick={(e) => updateSelected({ TypeOfPW: e.item.ID })}
      />
      <Button themeColor="primary" onClick={() => setEmailImageIds(selectedIds)}>E-Mail</Button>
      <Button themeColor="error" onClick={deleteSelected}>Delete {selectedIds.length} {selectedIds.length === 1 ? 'Image' : 'Images'}</Button>
    </Toolbar>}
    <Grid
      data={details?.Images}
      dataItemKey={DATA_ITEM_KEY}
      selectable
      select={selectedState}
      onSelectionChange={onSelectionChange}
      onHeaderSelectionChange={onHeaderSelectionChange}
    >
      <GridNoRecords>
        No images found.
      </GridNoRecords>
      <Column
        field={SELECTED_FIELD}
        width={34}
        columnType="checkbox"
        headerSelectionValue={
          details?.Images.findIndex((item) => !selectedState[idGetter(item)]) === -1
        }
      />
      <Column field="ImageID" title="Filename" width={130} editable={false} cells={{
        data: (props) => (
          <td {...props.tdProps}>
            <a
              style={{ color: '#007bff' }}
              href="#"
              onClick={(e) => {
                e.preventDefault();
                if (isMobile) {
                  window.open(`/Image/View/${props.dataItem.ImageID}`, '_blank');
                } else {
                  const idx = details?.Images.findIndex(x => x.ImageID === props.dataItem.ImageID) ?? -1;
                  setLightboxIndex(idx >= 0 ? idx : null);
                }
              }}
            >
              {props.dataItem.ImageID}-{props.dataItem.PageNum}
            </a>
            <a className="ml-2" href={`/Image/Download/${props.dataItem.ImageID}`}>
              <SvgIcon icon={downloadIcon} />
            </a>
            {
              props.dataItem.AIClassifiedDateTime && props.dataItem.AIClassifiedReason &&
              <span title={`AI Summary: ${props.dataItem.AIClassifiedReason}`} className="ml-2">
                <SvgIcon icon={infoCircleIcon} />
              </span>
            }
          </td>
        )
      }} />
      <Column field="TypeOfPW" title="Document Type" width={222} cells={{ data: DocumentTypeCell }} />
      <Column field="PrintFlag" title="Print w/ Invoice" cells={{ data: PrintWithInvoiceCell }} />
      <Column field="ImageDate" title="Indexed" editable={false} cells={{ data: ImageDateCell }} />
      <Column field="ImageSourceName" title="Source" editable={false} />
      <Column field="ModifiedByUserNumber" title="Modified By" editable={false} />
      <Column field="ModifiedDateTime" title="Modified Date" editable={false} cells={{ data: DateCell }} />
      <Column width={85} cells={{
        data: (props) => (
          <td {...props.tdProps} className={[props.tdProps?.className, "k-command-cell"].filter(Boolean).join(" ")}>
            <Button
              disabled={!props.dataItem.Edited}
              themeColor="primary"
              className="k-grid-edit-command"
              onClick={(e) => updateItem(props.dataItem)}
            >
              Update
            </Button>
          </td>
        )
      }} />
    </Grid>
    {selectedIds.length} {selectedIds.length === 1 ? 'image' : 'images'} selected
    <div className="mt-2">
      <Button
        themeColor="secondary"
        disabled={uploading}
        onClick={(e) => {
          openWindowPerc(`/Image/${orderIdParam}/ImageViewer`, .9, .9, 'ClientApp-ImageViewer');
          e.preventDefault();
        }}
      >
        Open Image Viewer
      </Button>
    </div>
  </>;
};

export default OrderImages;
